import React from "react";
import { ActionIcon, Box, Text } from "@mantine/core";
import { IconGripVertical, IconX } from "@tabler/icons-react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { moveItem } from "./customizeRules";
import Pill from "../../../common/Pill";
import styles from "./CustomizeDrawer.module.css";

export interface SortableItem {
  id: string;
  label: string;
}

export interface SortableListProps {
  items: SortableItem[];
  /** Accessible name of the list, e.g. "Selected groupings". */
  ariaLabel: string;
  onReorder: (ids: string[]) => void;
  onRemove: (id: string) => void;
  /** False hides the remove buttons (e.g. at the minimum count). */
  canRemove: boolean;
  /** Number shown on the first item's badge (Date takes 1 in Group by). */
  firstNumber?: number;
}

// Vertical-only dragging (no @dnd-kit/modifiers dependency), as in
// settings/StaffReorderPanel.
const lockToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

interface RowProps {
  item: SortableItem;
  number: number;
  canRemove: boolean;
  onRemove: (id: string) => void;
}

function SortableRow({ item, number, canRemove, onRemove }: RowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });
  return (
    <Box
      ref={setNodeRef}
      className={styles.selectedRow}
      data-dragging={isDragging || undefined}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <ActionIcon
        variant="subtle"
        color="gray"
        aria-label={`Drag ${item.label}`}
        className={styles.handle}
        {...attributes}
        {...listeners}
      >
        <IconGripVertical size={15} aria-hidden />
      </ActionIcon>
      <Pill tone="tint">{number}</Pill>
      <Text component="span" className={styles.rowLabel}>
        {item.label}
      </Text>
      {canRemove && (
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label={`Remove ${item.label}`}
          onClick={() => onRemove(item.id)}
        >
          <IconX size={14} aria-hidden />
        </ActionIcon>
      )}
    </Box>
  );
}

/** A numbered, drag-to-reorder list with remove buttons. */
export default function SortableList({
  items,
  ariaLabel,
  onReorder,
  onRemove,
  canRemove,
  firstNumber = 1,
}: SortableListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const ids = items.map((i) => i.id);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    const next = moveItem(ids, String(active.id), String(over.id));
    if (next !== ids) onReorder(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[lockToVerticalAxis]}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <Box role="list" aria-label={ariaLabel} className={styles.selectedList}>
          {items.map((item, i) => (
            <Box role="listitem" key={item.id}>
              <SortableRow
                item={item}
                number={firstNumber + i}
                canRemove={canRemove}
                onRemove={onRemove}
              />
            </Box>
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}
